import { getExtensionValue, MedplumClient } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import zip from "lodash/zip";

import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Extensions } from "@/fhir/enums/extensions";
import { NewbornSystemsQuestionnaires } from "@/fhir/enums/questionnaires";

// Simple in-memory cache for processed diff data
const diffCache = new Map<string, { data: NewbornSystemsDiffResult; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Generate cache key based on parameters
function generateCacheKey(
  encounterId: string,
  versionId?: string,
  currentVersionIndex?: number,
): string {
  return `newborn-systems-diff:${encounterId}:${versionId || "null"}:${currentVersionIndex ?? "null"}`;
}

// Check if cache entry is still valid
function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_TTL;
}

// Clear cache for a specific encounter (useful when data changes)
export function clearNewbornSystemsDiffCache(encounterId: string): void {
  const keysToDelete = Array.from(diffCache.keys()).filter((key) => key.includes(encounterId));
  keysToDelete.forEach((key) => diffCache.delete(key));
}

type SystemKey = keyof typeof NewbornSystemsQuestionnaires;

// Processed format for newborn systems
export interface NewbornSystemsSectionAnswer {
  type: "newborn-system";
  systemName: string;
  normal: boolean;
  notNormal: boolean;
  normalDetails: string[];
  notNormalDetails: string[];
  notes: string;
}

export type NewbornSystemsAnswer = NewbornSystemsSectionAnswer | undefined;

export type NewbornSystemsDiffResult = {
  [title: string]: NewbornSystemsAnswer[][];
};

// Helper function to parse newborn systems data
function parseNewbornSystemsAnswers(
  questionnaireResponse?: QuestionnaireResponse,
): NewbornSystemsAnswer[] {
  if (!questionnaireResponse?.item) return [];

  const answers: NewbornSystemsAnswer[] = [];

  // Get the global notes first
  const globalNotes =
    questionnaireResponse.item.find((item) => item.linkId === "notes")?.answer?.[0]?.valueString ||
    "";

  // Process each system in the questionnaire response
  for (const item of questionnaireResponse.item) {
    if (item.linkId === "notes") {
      // Skip the notes item as we handle it globally
      continue;
    }

    // This is a system section
    const systemName = item.linkId;
    const find = (linkId: string) => item.item?.find((subItem) => subItem.linkId === linkId);
    const getCoding = (linkId: string) =>
      find(linkId)
        ?.answer?.map((answer) => answer.valueCoding?.display || answer.valueCoding?.code)
        ?.filter((item): item is string => Boolean(item)) || [];

    const normal = find(`${item.linkId}Normal`)?.answer?.[0]?.valueBoolean ?? false;
    const notNormal = find(`${item.linkId}NotNormal`)?.answer?.[0]?.valueBoolean ?? false;
    const normalDetails = getCoding(`${item.linkId}NormalDetails`);
    const notNormalDetails = getCoding(`${item.linkId}NotNormalDetails`);

    answers.push({
      type: "newborn-system",
      systemName,
      normal,
      notNormal,
      normalDetails,
      notNormalDetails,
      notes: globalNotes, // Use the global notes for all systems
    });
  }

  return answers;
}

export async function getNewbornSystemsDiffHistory(
  medplum: MedplumClient,
  encounter: Encounter,
  versionId?: string,
  currentVersionIndex?: number,
): Promise<NewbornSystemsDiffResult> {
  // Check cache first
  const cacheKey = generateCacheKey(encounter.id!, versionId, currentVersionIndex);
  const cachedEntry = diffCache.get(cacheKey);

  if (cachedEntry && isCacheValid(cachedEntry.timestamp)) {
    return cachedEntry.data;
  }

  const result: NewbornSystemsDiffResult = {};

  // Get all newborn systems questionnaires
  const systems = [
    "Appearance",
    "Tone",
    "SkinAndBirthmarks",
    "HeadAndNeck",
    "Eyes",
    "MouthAndPalate",
    "Ears",
    "Abdomen",
    "HeartSounds",
    "Pulses",
    "Lungs",
    "Umbilicus",
    "Genitourinary",
    "Anus",
    "Hips",
    "Spine",
    "Musculoskeletal",
    "Reflexes",
  ] as const;

  // Fetch all newborn systems histories in parallel
  await Promise.all(
    systems.map(async (key) => {
      const url = NewbornSystemsQuestionnaires[key as SystemKey];
      const [history, title] = await fetchAmendmentHistoryByQuestionnaireUrl(
        medplum,
        url,
        encounter.id!,
      );

      if (!history || !title) return;

      // Apply version filtering similar to physical exams
      let currentQuestionnaireResponse: QuestionnaireResponse | undefined;
      let previousQuestionnaireResponse: QuestionnaireResponse | undefined;

      if (versionId) {
        // Find the questionnaire response that matches the versionId via extension
        const currentIndex = history.findIndex((qr) => {
          const parentVersionId = getExtensionValue(
            qr,
            Extensions.QuestionnaireResponseParentVersionId,
          );
          return parentVersionId === versionId;
        });

        if (currentIndex === -1) {
          // If no matching version found, this is likely an amendment scenario
          // where we're adding a new newborn system for the first time
          // In this case, we want to show the most recent version as "previous"
          // and have no "current" version (since we're creating it)
          currentQuestionnaireResponse = undefined;
          previousQuestionnaireResponse = history[0]; // Most recent version
        } else {
          currentQuestionnaireResponse = history[currentIndex];
          previousQuestionnaireResponse = history[currentIndex + 1];
        }
      } else if (currentVersionIndex !== undefined) {
        // Fallback to index-based lookup for backward compatibility
        const historyLength = history?.length;
        if (historyLength < currentVersionIndex + 1) return;

        currentQuestionnaireResponse = history[currentVersionIndex];
        previousQuestionnaireResponse = history[currentVersionIndex + 1] ?? history.at(-1);
      } else {
        // No version information provided, skip
        return;
      }

      // Process the filtered data into our normalized format
      const currentAnswers = parseNewbornSystemsAnswers(currentQuestionnaireResponse);
      const previousAnswers = parseNewbornSystemsAnswers(previousQuestionnaireResponse);

      // Only add to result if we have valid data
      if (currentAnswers.length > 0 || previousAnswers.length > 0) {
        const zippedData = zip(currentAnswers, previousAnswers).filter(
          ([current, previous]) => current !== undefined || previous !== undefined,
        );

        if (zippedData.length > 0) {
          result[title] = zippedData;
        }
      }
    }),
  );

  // Store result in cache
  diffCache.set(cacheKey, {
    data: result,
    timestamp: Date.now(),
  });

  return result;
}
